import React, { useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  Link,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccessTime,
  Computer,
  Payment,
  CheckCircle,
  Error,
  Warning,
  AttachMoney,
} from '@mui/icons-material';
import request from '../../services/Request';
import { useHistory } from 'react-router';
import LoadingIcon from '../items/buttons/LoadingIcon';
import moment from 'moment';

const getOrderByEmailURL = '/orders/getNewestTransaction/';

export default function ModernOrders(props) {
  const { id, loading, setLoading, order, setOrder, view_trans } = props;
  const history = useHistory();
  const theme = useTheme();
  const userData = JSON.parse(localStorage.getItem('user'));
  const user = userData.email;
  const userRole = userData.role;

  useEffect(() => {
    if (view_trans) {
      setLoading(true);
      let isCancelled = false;
      if (id) {
        async function getOrderByEmail() {
          await request({
            method: 'get',
            url: getOrderByEmailURL + user + '/10',
          }).then((res) => {
            if (!isCancelled) {
              setOrder(res.data);
              setLoading(false);
            }
          });
        }

        getOrderByEmail();
      }

      return () => {
        isCancelled = true;
      };
    }
  }, [props.id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <CheckCircle fontSize="small" />;
      case 'pending':
        return <Warning fontSize="small" />;
      case 'failed':
      case 'error':
        return <Error fontSize="small" />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {loading && <LoadingIcon />}

      {/* Header */}
      <Box sx={{ mb: 2.4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 38.4,
              height: 38.4,
              borderRadius: 1.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 3.2px 9.6px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <AttachMoney sx={{ color: 'white', fontSize: 22.4 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.2rem' }}>
              {}
              Recent Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {}
              Latest 10 transactions from your machines
            </Typography>
          </Box>
        </Box>
        <Link
          component="button"
          variant="body2"
          onClick={() => history.push('/dashboard/order')}
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: '0.7rem', // Scaled
            textDecoration: 'none',
            transition: 'all 0.3s',
            '&:hover': {
              textDecoration: 'underline',
              transform: 'translateX(3.2px)',
            },
          }}
        >
          View All →
        </Link>
      </Box>

      {/* Modern Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2.4,
          boxShadow: 'none',
          border: `0.8px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Table sx={{ '& .MuiTableCell-root': { padding: '8px 12.8px' } }}>
          <TableHead>
            <TableRow
              sx={{
                background:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <AccessTime sx={{ fontSize: '1rem' }} color="action" /> {}
                  Date
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Computer sx={{ fontSize: '1rem' }} color="action" /> {}
                  Kiosk
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Description</TableCell>{' '}
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Payment sx={{ fontSize: '1rem' }} color="action" /> {}
                  Payment
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Status</TableCell>{' '}
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.map((item, index) => {
              let date = new Date(item.createdAt);
              if (item.realTime) {
                date = moment.utc(date).format('lll');
              } else {
                date = moment(date).format('lll');
              }

              return (
                <TableRow
                  key={item._id}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      transform: 'scale(1.001)',
                      transition: 'all 0.2s',
                    },
                    borderBottom:
                      index === order.length - 1 ? 'none' : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.7rem' }}>
                      {date}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {item.machineID?.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ maxWidth: 160, fontSize: '0.7rem' }}
                    >
                      {/* 200*0.8, scaled */}
                      {item.machineID?.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.payment_type}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.6rem', // Scaled
                        height: 20, // Scaled chip height
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        border: `0.8px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(item.status)}
                      label={item.status}
                      size="small"
                      color={getStatusColor(item.status)}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.6rem', // Scaled
                        height: 20, // Scaled chip height
                        '& .MuiChip-icon': {
                          fontSize: '0.9rem', // Scaled icon
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        color: theme.palette.success.main,
                        fontSize: '0.8rem',
                      }}
                    >
                      ${item.total}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {order.length === 0 && !loading && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6.4,
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            No transactions found
          </Typography>{' '}
          {}
          <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
            Start selling to see your transactions here
          </Typography>{' '}
          {}
        </Box>
      )}
    </Box>
  );
}
